import axios from 'axios';
import $ from "jquery";
import moment from 'moment';
import React,{ useEffect, useRef, useState } from 'react';
import HtmlParser from 'react-html-parser';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { approvedRejectEvidence, deleteEvidence, getUsersAnswers, updateSubmissionData, uploadEvidence } from '../../../../services/CourseService';
import { AWS_BASE_URL, downloadURL, IMAGE_URL } from '../../../../utils/Constants';
import { twoDecimalRound } from '../../../../utils/commonFunction';
import PermissionsGate from '../../../../utils/permissionGate';
import { handleUploadToS3BucketUrl } from '../../../common/uploadTOS3BucketUrl';
import DownloadModalComponent from './DownloadModalComponent';
import { createCleanFilePath } from '../../../../utils/FileDownload';
import { isBlockedNumericKey } from '../../../common/Helper';

/**
 * Options for evidence types in submission modal
 * Includes Photo, Video, and Lecturer Observation
 *
 * @constant
 * @type {Array<Object>}
*/
const evidenceOptions = [
  { label: "Photo Evidence", value: 0, icon: "fal fa-camera", name: "photo" },
  { label: "Video Evidence", value: 1, icon: "fal fa-video", name: "video" },
  { label: "Lecturer Observation", value: 2, icon: "fal fa-user-check", name: "observation" },
];

/**
 * Modal component for viewing and grading student submissions
 * Handles evidence uploads (photo/video), marking, feedback, and various question types
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Object} props.selectedStudentDetails - Current selected student submission data
 * @param {Function} props.setSelectedStudentDetails - Setter for selected student
 * @param {Function} props.handleDownload - Function to handle file downloads
 * @param {Function} props.handleRefresh - Function to refresh parent data after changes
 * @param {Function} props.isMatchingQuestionType - Checks if question matches given types
 * @param {Object} props.questionData - Question configuration and options
 * @param {Object} props.studentDetails - Student information
 * @param {Object} props.assDetails - Assessment details including grading type
 * @param {string} props.tabName - Current tab name for API calls
 * @param {Object} props.assessmentHeader - Assessment header with permissions
 * @param {Object} props.downloadRef - Reference for download modal
 * @returns {JSX.Element} Rendered submission modal
 */
const SubmissionModal = ({
  selectedStudentDetails,
  setSelectedStudentDetails,
  handleDownload,
  handleRefresh,
  isMatchingQuestionType,
  questionData,
  studentDetails,
  assDetails,
  tabName,
  assessmentHeader,
  downloadRef,
  shouldLockAssessment,
}) => {
  const { subId} = useParams();
  const [groupedEvidenceAttachments, setGroupedEvidenceAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ photo: {}, video: {} });
  const [isUploading, setIsUploading] = useState({ photo: false, video: false });
  const [savingData, setSavingData] = useState(false);
  const [userAnswerMatrixData, setUserAnswerMatrixData] = useState([]);
  const [correctAnswerMatrixData, setCorrectAnswerMatrixData] = useState([]);
  const [userSelectedAnswerDetails, setUserSelectedAnswerDetails] = useState([])
  const [error, setError] = useState(false)
  const [hasDataChanged, setHasDataChanged] = useState(false)
  const [evidenceData, setEvidenceData] = useState({})
  const [evidenceStatusUpdated, setEvidenceStatusUpdated]= useState(false)
  const [evidenceLoading, setEvidenceLoading] = useState(false)
  const [expanded, setExpanded] = useState({
    questionExpand: false,
    questionAnswerExpand: false
  });
  const [showSeeMore, setShowSeeMore] = useState({
    questionExpandSeeMore: false,
    questionAnswerExpandSeeMore: false
  });

  const [updatedData, setUpdatedData] = useState({
    score: null,
    user_feedback: "",
    is_photo_accept: 0,
    is_video_accept: 0,
    is_observation_accept: 0,
  });

  const descriptionRef = useRef(null);
  const answersRef = useRef(null);


  /**
   * Closes the modal and resets all form states
   * Triggers parent refresh if data was changed
   *
   * @function modalClose
   * @returns {void}
  */
  const modalClose = () => {
    setEvidenceStatusUpdated(false)
    setUpdatedData({
      score: null,
      user_feedback: "",
      is_photo_accept: 0,
      is_video_accept: 0,
      is_observation_accept: 0,
    })
    setSelectedStudentDetails({});
    setError(false)
    setUploadProgress({ photo: {}, video: {} })
    setIsUploading({ photo: false, video: false })
    setSavingData(false)
    setUserAnswerMatrixData([])
    setCorrectAnswerMatrixData([])
    setUserSelectedAnswerDetails([])
    setGroupedEvidenceAttachments([])
    setExpanded({
      questionExpand: false,
      questionAnswerExpand: false
    });
    setShowSeeMore({
      questionExpandSeeMore: false,
      questionAnswerExpandSeeMore: false
    });
    if(hasDataChanged ){
      handleRefresh()
    }
    $("#assessment-submission-modal").modal("hide");
  }

  /**
   * Groups evidence attachments by type (photo, video, observation)
   * Also initializes form data from selected student details
   *
   * @function groupEvidences
   * @param {Array<Object>} studentEvidences - Array of student evidence objects
   * @param {Object} [selectedToggle] - Optional toggle state for evidence acceptance
   * @returns {void}
  */
  const groupEvidences = (studentEvidences,selectedToggle) => {
    const grouped = evidenceOptions?.map(option => {
      const matchingItems = studentEvidences?.filter(
        item => item.evidence_type === option.value
      );

      let acceptanceDetails = {};
      let show = true
      if (option.name === 'photo') {
        show = selectedStudentDetails?.evidence_photo == 1 ? true : false
        acceptanceDetails = {
          name: selectedStudentDetails?.photoAcceptanceDetail?.fullname || '',
          date: selectedStudentDetails?.photo_accepted_date || null,
        };
      } else if (option.name === 'video') {
        show = selectedStudentDetails?.evidence_video == 1 ? true : false
        acceptanceDetails = {
          name: selectedStudentDetails?.videoAcceptanceDetail?.fullname || '',
          date: selectedStudentDetails?.video_accepted_date || null,
        };
      } else if (option.name === 'observation') {
        show = selectedStudentDetails?.evidence_lecturer_observation == 1 ? true : false
        acceptanceDetails = {
          name: selectedStudentDetails?.is_observation == 1 ? selectedStudentDetails?.observationBy?.fullname : '',
          date: selectedStudentDetails?.is_observation == 1 ? selectedStudentDetails?.observation_date : null
        };
      }
      return {
        label: option.label,
        icon: option.icon,
        name: option.name,
        value: option.value,
        items: matchingItems?.sort((a, b) => a.is_admin - b.is_admin),
        pendingUploads: [],
        acceptanceDetails,
        show
      };
    });

    setGroupedEvidenceAttachments(grouped);

    // Initialize form with student details
    if (selectedStudentDetails && !selectedToggle) {
      setUpdatedData({
        score: (selectedStudentDetails.is_marked || !isMatchingQuestionType(["Document upload", "Long Answer", "Evidence Only"])) && selectedStudentDetails.score != null
          ? (selectedStudentDetails.score % 1 !== 0
            ? Number(selectedStudentDetails.score).toFixed(2)
            : selectedStudentDetails.score)
          : null,

        user_feedback: selectedToggle?.user_feedback ? selectedToggle.user_feedback : selectedStudentDetails.user_feedback || "",
        is_photo_accept: selectedToggle?.is_photo_accept ? selectedToggle?.is_photo_accept : selectedStudentDetails.is_photo_accept || 0,
        is_video_accept: selectedToggle?.is_video_accept ? selectedToggle?.is_video_accept : selectedStudentDetails.is_video_accept || 0,
        is_observation_accept: selectedToggle?.is_observation_accept ? selectedToggle?.is_observation_accept : selectedStudentDetails.is_observation || 0,
      });
    }
  }

  /**
   * Groups evidence attachments when selected student changes
   * Also initializes form data with student's existing scores/feedback
   *
   * @effect
   * @dependencies selectedStudentDetails - Runs when selected student changes
   * @returns {void}
  */
  useEffect(() => {
    if (selectedStudentDetails) {
      groupEvidences(selectedStudentDetails?.studentEvidence)
    }
  }, [selectedStudentDetails]);

  /**
   * Checks if question description or answer text overflows container
   * Updates showSeeMore state to conditionally show "See More" button
   *
   * @effect
   * @returns {void}
  */
  useEffect(() => {
    const checkOverflow = () => {
      const questionOverflow = descriptionRef.current
        ? descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight
        : false;

      const answerOverflow = answersRef.current
        ? answersRef.current.scrollHeight > answersRef.current.clientHeight
        : false;

      setShowSeeMore((prev) => ({
        ...prev,
        questionExpandSeeMore: questionOverflow,
        questionAnswerExpandSeeMore: answerOverflow,
      }));
    };


    if (selectedStudentDetails) {
      setTimeout(() => {
        checkOverflow();
      }, 300);
    } else {
      setShowSeeMore({ questionExpandSeeMore: false, questionAnswerExpandSeeMore: false });
      setExpanded({ questionExpand: false, questionAnswerExpand: false });
    }
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [selectedStudentDetails]);


  /**
   * Toggles expanded/collapsed state for question description or answer text
   *
   * @function toggleShowMore
   * @param {string} key - Key to toggle ('questionExpand' or 'questionAnswerExpand')
   * @returns {void}
  */
  const toggleShowMore = (key) => {
    setExpanded(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  }

  /**
   * Handles file upload for photo or video evidence
   * Validates file type, uploads to S3, and updates evidence list
   *
   * @async
   * @function handleUploadFile
   * @param {Event} e - File input change event
   * @param {string} evidenceName - Type of evidence ('photo' or 'video')
   * @returns {Promise<void>}
  */
  const handleUploadFile = async (e, evidenceName) => {
    const files = e.target.files;
    console.log("files>>>",files)
    if (!files || files.length === 0) return;
    if (evidenceName !== 'photo' && evidenceName !== 'video') return;

    const validFiles = Array.from(files).filter(file => {
      if (evidenceName === 'photo' && !file.type.startsWith('image/')) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File',
          text: `${file.name} is not an image file`
        });
        return false;
      }
      if (evidenceName === 'video' && !file.type.startsWith('video/')) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File',
          text: `${file.name} is not a video file`
        });
        return false;
      }
      setIsUploading(prev => (
        {
          ...prev,
          [evidenceName]: true
        }));
      setUploadProgress(prev => (
        {
          ...prev,
          [evidenceName]: {}
        }));
      setHasDataChanged(true)
      return true;

    });

    // Initialize progress for each file
    validFiles.forEach(file => {
      setUploadProgress(prev => ({
        ...prev,
        [evidenceName]: { ...prev[evidenceName], [file.name]: 0 }
      }));
    });

    for (const file of validFiles) {
      let awsFileNameObj=[]

    let filePath=`${file?.name?.split(".")?.[0]}_evidence_${moment().unix()}.${file?.name?.split(".").pop()}`
    awsFileNameObj.push({
      file_name:filePath,
      file_type: file?.type
    })
      const result = await handleUploadToS3BucketUrl(awsFileNameObj, file)
      if(!result) return
      
      const formData = new FormData();
      formData.append('assessment_id', selectedStudentDetails?.assessment_id);
      formData.append('student_id', selectedStudentDetails?.uid);
      formData.append('qid', selectedStudentDetails?.qid);
      formData.append('evidence_type', evidenceName === "photo" ? 0 : 1);
      formData.append('attempt_id', selectedStudentDetails?.rid);

      result.forEach((item)=>{
        formData.append('evidence', item.path);
        formData.append('evidence_size', item.size);
      })

      try {
        const response = await uploadEvidence(formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({
              ...prev,
              [evidenceName]: { ...prev[evidenceName], [file.name]: percent }
            }));
          }
        });
        groupEvidences(response?.data?.getEvidences,{...updatedData})
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
      }
    }


    setIsUploading(prev => ({ ...prev, [evidenceName]: false }));
    setUploadProgress(prev => ({ ...prev, [evidenceName]: {} }));
  };

  /**
   * Saves the submission score and feedback to the server
   * Validates input before saving and shows success/error messages
   *
   * @async
   * @function handleSaveSubmission
   * @returns {Promise<void>}
  */
  const handleSaveSubmission = async () => {
    try {
      if (error) return
      setSavingData(true);
      const payload = {
        qid: selectedStudentDetails?.qid,
        updated_qid: selectedStudentDetails?.id,
        score: updatedData.score != null || updatedData.score != undefined ? updatedData.score : "a",
        rid: selectedStudentDetails?.rid,
        user_feedback: updatedData.user_feedback,
        tab: tabName
      };

      const res = await updateSubmissionData(payload);

      Swal.fire({
        icon: 'success',
        title: res?.data?.message,
      });
      handleRefresh()
      setSavingData(false);
    } catch (error) {
      console.error("Error updating submission:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update submission data.',
      });
      setSavingData(false);
    }
  };

  const handleToggleEvidence = (evidenceType) => {
    setEvidenceLoading(true)
    let key = '';
    let currentVal = 0;
    let newValue = 0;

    switch (evidenceType) {
      case 'photo':
        key = 'is_photo_accept';
        currentVal = updatedData.is_photo_accept;
        newValue = currentVal ? 0 : 1;
        break;
      case 'video':
        key = 'is_video_accept';
        currentVal = updatedData.is_video_accept;
        newValue = currentVal ? 0 : 1;
        break;
      case 'observation':
        key = 'is_observation_accept';
        currentVal = updatedData.is_observation_accept;
        newValue = currentVal ? 0 : 1;
        break;
      default:
        return;
    }

    const payload = {
      updated_qid: selectedStudentDetails?.id,
      rid: selectedStudentDetails?.rid,
      [key]: newValue,
      tab: tabName
    };

    approvedRejectEvidence(payload)
      .then((res) => {
        if (res.status === 200 && res.data?.result) {
          setEvidenceData(res.data.result);
          setUpdatedData(prev => ({
            ...prev,
            [key]: newValue
          }));
          setEvidenceStatusUpdated(true)
          setHasDataChanged(true)
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: `Failed to update evidence ${evidenceType}`,
          });
        }
      })
      .catch((err) => {
        console.error("Error updating evidence:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Failed to update evidence ${evidenceType}`,
        });
      }).finally(()=>setEvidenceLoading(false));
  };
  
  /**
   * Deletes an uploaded evidence file
   * Refreshes evidence list after successful deletion
   *
   * @async
   * @function handleDeleteFile
   * @param {Object} fileInfo - File information object containing id, assessment_id, qid, attempt_id
   * @returns {Promise<void>}
  */
  const handleDeleteFile = async (fileInfo) => {
    try {
      const payload = {
        id: fileInfo?.id,
        assessment_id: fileInfo?.assessment_id,
        qid: fileInfo?.qid,
        attempt_id: fileInfo?.attempt_id,
        tab: tabName
      }

      if (fileInfo) {
        const res = await deleteEvidence(payload);
        groupEvidences(res?.data?.getEvidences,{...updatedData})
        setHasDataChanged(true)
        Swal.fire(
          'Deleted!',
          'File has been deleted.',
          'success'
        );
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      Swal.fire(
        'Error',
        'Failed to delete the file. Please try again.',
        'error'
      );
    }
  };

  /**
   * Converts user's answers into matrix format for Choice Matrix questions
   * Triggers only when question type is "Choice Matrix" and data exists
   *
   * @effect
   * @dependencies [userSelectedAnswerDetails, selectedStudentDetails]
   * @returns {void}
  */
  useEffect(() => {
    if (
      isMatchingQuestionType(['Choice Matrix']) &&
      userSelectedAnswerDetails?.length > 0 && Object.keys(selectedStudentDetails)?.length > 0 
    ) {
      const matrixData = userSelectedAnswerDetails?.map((item, key) => {
        let parsedOptions = [];

        try {
          parsedOptions = item.q_option ? JSON.parse(item.q_option) : [];
        } catch (e) {
          console.error('Error parsing q_option:', e);
        }
        return {
          rowTitle: item.titleValue,
          options: parsedOptions.map((opt) => ({
            ...opt,
            text: opt.titleValue,
            isSelected: selectedStudentDetails?.UserAnswer[key]?.split("___")[1] == opt?.titleValue,
          })),
        };
      });

      setUserAnswerMatrixData(matrixData);
    }
  }, [userSelectedAnswerDetails, selectedStudentDetails]);

  /**
   * Converts correct answers into matrix format for Choice Matrix questions
   * Used to display correct answers for comparison
   *
   * @effect
   * @dependencies [questionData, selectedStudentDetails]
   * @returns {void}
  */
  useEffect(() => {
    if (
      isMatchingQuestionType(['Choice Matrix']) &&
      Array.isArray(questionData?.questionsOptions)
    ) {
      const mappedCorrectMatrix = questionData.questionsOptions.map((item) => {
        let parsedOptions = [];

        try {
          parsedOptions = item.q_option ? JSON.parse(item.q_option) : [];
        } catch (e) {
          console.error('Error parsing q_option:', e);
        }

        return {
          rowTitle: item.titleValue,
          options: parsedOptions.map((opt) => ({
            ...opt,
            text: opt.titleValue,
            isSelected: opt.isSelected || false,
          })),
        };
      });

      setCorrectAnswerMatrixData(mappedCorrectMatrix);
    }
  }, [questionData, selectedStudentDetails]);


  /**
   * Fetches user's selected answers for matrix/choice questions
   * Used for displaying submitted answers in modal
   *
   * @async
   * @function fetchUsersAnswers
   * @param {Object} cancelToken - Axios cancel token for request cancellation
   * @returns {Promise<void>}
  */
  const fetchUsersAnswers = async (cancelToken) => {
    try {
      const payload = {
        isDuplicateSearched: 1,
        qId: selectedStudentDetails?.id,
      }
      const res = await getUsersAnswers(payload, cancelToken)
      setUserSelectedAnswerDetails(res?.data)
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error(error);
      }
    }
  }

  /**
   * Fetches user's selected answers from API when a student is selected
   * Cancels previous request if component unmounts or new student selected
   *
   * @effect
   * @dependencies selectedStudentDetails.id - Runs when student ID changes
   * @returns {void}
  */
  useEffect(() => {
    const questionSource = axios.CancelToken.source()

    if (selectedStudentDetails?.id) {
      fetchUsersAnswers(questionSource.token)
    }

    return () => {
      questionSource.cancel('Cancelled due to component unmount or dependency change')
    }
  }, [selectedStudentDetails])


  /**
   * Renders a matrix table for Choice Matrix question type
   * Displays rows with radio button options for each column
   *
   * @component
   * @param {Object} props - Component properties
   * @param {Array<Object>} props.matrixArray - Matrix data with rows and options
   * @param {string} props.title - Title for the matrix (Submitted/Correct Answer)
   * @returns {JSX.Element} Rendered matrix table
  */
  const MatrixTable = ({ matrixArray, title }) => (
    <div className="view-matrix-table choice-matrix matrix-submission mb-4">
      <table className="export-table-border-header choice-matrix-table ">
        <thead className='submission-matrix-head'>
          <tr>
            <th></th>
            {matrixArray?.[0]?.options?.map((option, index) => (
              <th className='text-center' key={index}>{option.text}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrixArray?.map((rowData, rowIndex) => (
            <tr key={rowIndex}>
              <td>{rowData?.rowTitle}</td>
              {rowData?.options?.map((option, optionIndex) => (
                <td key={optionIndex}>
                  <div className="form-icon-group justify-content-center">
                      <input
                        type="radio"
                        className="cursor-pointer"
                        name={`${title}-${rowIndex}`}
                        readOnly
                        checked={option?.isSelected || false}
                      />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /**
   * Renders the student's submitted answer based on question type
   * Supports Document Upload, Long Answer, Match the Column, Multiple Choice,
   * Short Answer, Fill in the Blank, Choice Matrix, and Order List
   *
   * @component
   * @returns {JSX.Element} Rendered answer based on question type
  */
  const SubmittedAnswer = () => {
    return (
      <>
        {isMatchingQuestionType(["Document Upload"]) && selectedStudentDetails?.answerdDocument?.length > 0 && (
          <DownloadModalComponent children={
          <button
            className="btn btn-primary"
            data-toggle="modal" data-target="#downloadSubmissionOfModel"
            title="Download Submission"
          >
            <i className="fal fa-download"></i> Download Submission
          </button>
          } modalId={'downloadSubmissionOfModel'} payload={handleDownload([selectedStudentDetails], true, `evidence_${selectedStudentDetails?.studentName}_${Date.now()}`)} downloadRef={downloadRef} zipName={`${selectedStudentDetails?.studentName}_${assessmentHeader?.name}`}/>
        )}

        {isMatchingQuestionType(["Long Answer"]) && selectedStudentDetails?.UserAnswer?.length > 0 && (
          <>
            <p
              ref={answersRef}
              className={`description-p questionDescription ${expanded?.questionAnswerExpand ? 'expanded' : 'collapsed'}`}
            > {selectedStudentDetails?.UserAnswer} </p>

            {showSeeMore?.questionAnswerExpandSeeMore && (
              <span onClick={() => toggleShowMore("questionAnswerExpand")} className="toggle-button">
                {expanded?.questionAnswerExpand ? 'See Less' : 'See More'}
              </span>
            )}
          </>
        )}

        {isMatchingQuestionType(['Match the Column']) && (
          <div className={`assessement-question-desc description-container mt-3`}>
            <div className="row">
              <div className="col-6">
                <h6 className="question-title mt-0">Submitted Answer</h6>
                <div className={`match-column-con`}>
                  {selectedStudentDetails?.userSubmittedAns?.length > 0 ? selectedStudentDetails?.userSubmittedAns
                    ?.map((answer, index) => (
                      <div key={index} className='match-column-wrp'>
                        <span className={`match-column-input ${answer.q_option?.split("___")[0] ? "" : "hide-border"} `}>{answer.q_option?.split("___")[0]}</span>
                        <span className={`match-column-input ${answer.q_option?.split("___")[1] ? "dashed" : "hide-border"}`}>{answer.q_option?.split("___")[1]}</span>
                      </div>
                    )) : "-"}
                </div>
              </div>
              <div className="col-6">
                <div className={`match-column-con`}>
                  <h6 className="question-title mt-0">Corrected Answer</h6>
                  {questionData?.questionsOptions ? questionData?.questionsOptions
                    ?.map((answer, index) => (
                      <div key={index} className='match-column-wrp'>
                        <span className={`match-column-input ${answer?.q_option?.length > 0 ? "" : "hide-border"}`}>{answer.q_option}</span>
                        <span className={`match-column-input ${answer?.q_option_match?.length > 0 ? "dashed" : "hide-border"} `}>{answer.q_option_match}</span>
                      </div>
                    )) : "-"}
                </div>
              </div>
            </div>
          </div>
        )}

        {isMatchingQuestionType(["Multiple Choice Multiple Answer", "Multiple Choice Single Answer"]) && (

          <div className={`assessement-question-desc description-container mt-3`}>
            <div className="row">
              <div className="col-6">
                <h6 className="question-title mt-0">Submitted Answer</h6>
                <div className={`match-column-con`}>
                  {userSelectedAnswerDetails?.length > 0 ? userSelectedAnswerDetails?.map((answer, index) => (
                    <div className="" key={index}>
                      <input
                        type={isMatchingQuestionType(["Multiple Choice Multiple Answer"]) ? "checkbox" : "radio"}
                        className="cursor-pointer"
                        name="example1"
                        checked={isMatchingQuestionType(["Multiple Choice Multiple Answer"])
                          ? selectedStudentDetails?.UserChooseOptionId?.find((id) => id == answer?.oid ? true : false)
                          : selectedStudentDetails?.UserChooseOptionId == answer?.oid ? true : false
                        }
                        readOnly
                      />

                      <label className="ml-2 mb-0 cursor-pointer" htmlFor={`customRadio_`}>
                        {answer.q_option ? HtmlParser(answer.q_option) : ""}
                      </label>
                    </div>
                  )) : "-"}
                </div>
              </div>
              <div className="col-6">
                <div className={`match-column-con`}>
                  <h6 className="question-title mt-0">Corrected Answer</h6>
                  {questionData?.questionsOptions?.length > 0 ? questionData?.questionsOptions?.map((answer, index) => (
                    <div className="" key={index}>
                      <input
                        type={isMatchingQuestionType(["Multiple Choice Multiple Answer"]) ? "checkbox" : "radio"}
                        className="cursor-pointer"
                        name="example2"
                        checked={answer.score}
                        readOnly
                      />

                      <label className="ml-2  mb-0 cursor-pointer" htmlFor={`customRadio_`}>
                        {answer.q_option ? HtmlParser(answer.q_option) : ""}
                      </label>
                    </div>
                  )) : "-"}
                </div>
              </div>
            </div>
          </div>
        )}

        {isMatchingQuestionType(['Short Answer']) && (
          <div className={`assessement-question-desc description-container mt-3`}>
            <div className="row">
              <div className="col-6">
                <h6 className="question-title mt-0">Submitted Answer</h6>
                <div className={`match-column-con`}>
                  <span>{selectedStudentDetails?.UserAnswer ? selectedStudentDetails?.UserAnswer : "-"}</span>
                </div>
              </div>
              <div className="col-6">
                <div className={`match-column-con`}>
                  <h6 className="question-title mt-0">Corrected Answer</h6>
                  <span>{selectedStudentDetails?.correctAnswer ? selectedStudentDetails?.correctAnswer?.replaceAll("~@~", ", ") : "-"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {isMatchingQuestionType(['Fill in the Blank']) && (
          <div className={`assessement-question-desc description-container mt-3`}>
            <div className="row">
              <div className="col-6">
                <h6 className="question-title mt-0">Submitted Answer</h6>
                <div className={`match-column-con`}>
                  {selectedStudentDetails?.UserAnswer ? <span dangerouslySetInnerHTML={{ __html: selectedStudentDetails?.UserAnswer }} /> : "-"}
                </div>
              </div>
              <div className="col-6">
                <div className={`match-column-con`}>
                  <h6 className="question-title mt-0">Corrected Answer</h6>
                  {selectedStudentDetails?.correctAnswer ? selectedStudentDetails?.correctAnswer?.split("___")?.map((answer, index) => (
                    <span key={index} dangerouslySetInnerHTML={{ __html: answer }} />
                  )) : "-"}

                </div>
              </div>
            </div>
          </div>
        )}

        {isMatchingQuestionType(['Choice matrix']) && (
          <div className={`assessement-question-desc description-container mt-3`}>
            <div className="row">
              <div className={userAnswerMatrixData?.[0]?.options?.length < 5 ? " col-9" : "col-12"}>
                <h6 className="question-title mt-0">Submitted Answer</h6>
                {userAnswerMatrixData.length > 0 && (
                  <MatrixTable matrixArray={userAnswerMatrixData} title="Submitted Answer" />
                )}
              </div>
              <div className={correctAnswerMatrixData?.[0]?.options?.length < 5 ? " col-9" : "col-12"}>
                <div className={`match-column-con pl-2`}>
                  <h6 className="question-title mt-0">Corrected Answer</h6>
                  {correctAnswerMatrixData.length > 0 && (
                    <MatrixTable matrixArray={correctAnswerMatrixData} title="Correct Answer" />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {isMatchingQuestionType(['Order list']) && (
          <div className={`assessement-question-desc description-container mt-3`}>
            <div className="row">
              <div className="col-6">
                <h6 className="question-title mt-0">Submitted Answer</h6>
                <div className={`match-column-con correct-answer`}>
                  {selectedStudentDetails?.UserAnswer ? selectedStudentDetails?.UserAnswer?.split(",")?.map((answer, index) => (
                    <div key={index} className='match-column-wrp'>
                      <span
                        className={`match-column-input`}
                        dangerouslySetInnerHTML={{ __html: answer?.split("___")[1] }}
                      />
                    </div>
                  )) : "-"}

                </div>
              </div>
              <div className="col-6">
                <div className={`match-column-con correct-answer`}>
                  <h6 className="question-title mt-0">Corrected Answer</h6>

                  {questionData?.questionsOptions?.length > 0 ? questionData?.questionsOptions?.map((answer, index) => (
                    <div key={index} className='match-column-wrp'>
                      <span className={`match-column-input ${answer?.q_option?.length > 0 ? "" : "hide-border"}`} dangerouslySetInnerHTML={{ __html: answer.q_option }} />
                    </div>
                  )) : "-"}
                </div>
              </div>
            </div>
          </div>
        )}


      </>
    )
  }


  return (
    <div
      className={`assessment-document-view modal fade mt-1`}
      id="assessment-submission-modal"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="exampleModalCenterTitle"
      aria-hidden="true"
      data-backdrop="static"
      data-keyboard="false"
    >
      <div
        className="modal-dialog modal-dialog-centered modal-xl"
        role="document"
      >
        <div className="modal-content modal-border-update">

          <div className="modal-body p-0">
            <div className="modal-header modal-header-custom">
              <h5 className="modal-title">
                <i className="fal fa-file-alt"></i> Submission: {selectedStudentDetails?.studentName}
              </h5>
              <button
                type="button"
                className="close"
                onClick={() => modalClose()}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="p-30px">
              <div>
                <div className={`assessement-question-desc description-container fontsize-16`}>
                  <h6 className="question-title mt-0">Question</h6>
                  <p
                    ref={descriptionRef}
                    className={`description-p questionDescription ${expanded?.questionExpand ? 'expanded' : 'collapsed'}`}
                    dangerouslySetInnerHTML={{ __html: selectedStudentDetails?.question || '' }}
                  />

                  {showSeeMore?.questionExpandSeeMore && (
                    <span onClick={() => toggleShowMore("questionExpand")} className="toggle-button">
                      {expanded?.questionExpand ? 'See Less' : 'See More'}
                    </span>
                  )}
                </div>

                {isMatchingQuestionType(["Document Upload", "Long Answer", "Evidence Only"]) && (
                  <div className={`assessement-question-desc description-container fontsize-16`}>
                    <h6 className="question-title mt-3">Submission</h6>
                  </div>
                )}

                <SubmittedAnswer />

                {groupedEvidenceAttachments?.some((item) => item?.show) &&

                  <div className='row mt-4'>
                    {groupedEvidenceAttachments?.map((evidence, key) => (
                      evidence?.show ?
                        (() => {
                          const visibleItems = groupedEvidenceAttachments?.filter(item => item?.show);
                          const lastVisibleName = visibleItems?.[visibleItems.length - 1]?.name;

                          return <div
                            className={`col-md-4 document_con ${evidence.name === lastVisibleName ? "last" : ""}`}
                            key={key}
                          >
                            <div className='main-wrp'>
                              <div className='document_wrp'>
                                <i className={`${evidence?.icon} evidence-color`} />
                                <p className='mb-0'>{evidence?.label}</p>
                                {(evidence.name === 'photo' && evidence?.items?.length > 0) ||
                                  (evidence.name === 'video' && evidence?.items?.length > 0) ||
                                  (evidence.name === 'observation' && updatedData.is_observation_accept) ? (
                                  <i className="fas fa-check-circle cat-greenhex"></i>
                                ) : (
                                  <i className="fas fa-times-circle colorRed"></i>
                                )}
                              </div>
                            </div>
                            <div className='main-wrp'>
                              <div className='document_wrp'>
                                <p className='mb-0 mr-4'>{evidence.name === "observation" ? "Confirm Observation" : "Accept Submitted Evidence"}</p>
                                <div className={`toggle-switch ${evidenceLoading  ? "disabled-click" : ""}`}>
                                  <label className="switch ml-0">
                                    <PermissionsGate scopes={assessmentHeader?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                                      <input
                                        type="checkbox"
                                        checked={(() => {
                                          let isAccepted = false;
                                          switch (evidence.name) {
                                            case 'photo':
                                              // Check if evidenceStatusUpdated is true, use evidenceData; otherwise use updatedData
                                              isAccepted = evidenceStatusUpdated
                                                ? String(evidenceData.is_photo_accept) === "1"
                                                : (updatedData.is_photo_accept !== undefined
                                                  ? String(updatedData.is_photo_accept) === "1"
                                                  : String(evidenceData.is_photo_accept) === "1");
                                              break;

                                            case 'video':
                                              isAccepted = evidenceStatusUpdated
                                                ? String(evidenceData.is_video_accept) === "1"
                                                : (updatedData.is_video_accept !== undefined
                                                  ? String(updatedData.is_video_accept) === "1"
                                                  : String(evidenceData.is_video_accept) === "1");
                                              break;

                                            case 'observation':
                                              isAccepted = evidenceStatusUpdated
                                                ? String(evidenceData.is_observation) === "1"
                                                : (updatedData.is_observation_accept !== undefined
                                                  ? String(updatedData.is_observation_accept) === "1"
                                                  : String(evidenceData.is_observation_accept) === "1");
                                              break;

                                            default:
                                              isAccepted = false;
                                          }
                                          return isAccepted; 
                                        })()}
                                        onChange={() => handleToggleEvidence(evidence.name)}
                                        className=''
                                        disabled={shouldLockAssessment}
                                      />
                                    </PermissionsGate>
                                    <span className="slider slider-round"></span>
                                  </label>
                                </div>
                              </div>
                              {(() => {
                                // Isolated acceptance status check for each evidence type
                                let isAccepted = false;
                                let confirmedDate = null;
                                let confirmedBy = null;

                                switch (evidence.name) {
                                  case 'photo':
                                    // Check if evidenceStatusUpdated is true, use evidenceData; otherwise use updatedData
                                    isAccepted = evidenceStatusUpdated
                                      ? String(evidenceData.is_photo_accept) === "1"
                                      : (updatedData.is_photo_accept !== undefined
                                        ? String(updatedData.is_photo_accept) === "1"
                                        : String(evidenceData.is_photo_accept) === "1");
                                    // When evidenceStatusUpdated is true, use evidenceData; otherwise fallback to evidence object
                                    confirmedDate = evidenceStatusUpdated
                                      ? evidenceData?.photo_accepted_date
                                      : (evidenceData?.photo_accepted_date || evidence?.acceptanceDetails?.date);
                                    confirmedBy = evidenceStatusUpdated
                                      ? evidenceData?.photoAcceptanceDetail?.fullname
                                      : (evidenceData?.photoAcceptanceDetail?.fullname || evidence?.acceptanceDetails?.name);
                                    break;

                                  case 'video':
                                    isAccepted = evidenceStatusUpdated
                                      ? String(evidenceData.is_video_accept) === "1"
                                      : (updatedData.is_video_accept !== undefined
                                        ? String(updatedData.is_video_accept) === "1"
                                        : String(evidenceData.is_video_accept) === "1");
                                    confirmedDate = evidenceStatusUpdated
                                      ? evidenceData?.video_accepted_date
                                      : (evidenceData?.video_accepted_date || evidence?.acceptanceDetails?.date);
                                    confirmedBy = evidenceStatusUpdated
                                      ? evidenceData?.videoAcceptanceDetail?.fullname
                                      : (evidenceData?.videoAcceptanceDetail?.fullname || evidence?.acceptanceDetails?.name);
                                    break;

                                  case 'observation':
                                    isAccepted = evidenceStatusUpdated
                                      ? String(evidenceData.is_observation) === "1"
                                      : (updatedData.is_observation_accept !== undefined
                                        ? String(updatedData.is_observation_accept) === "1"
                                        : String(evidenceData.is_observation_accept) === "1");
                                    confirmedDate = evidenceStatusUpdated
                                      ? evidenceData?.observation_date
                                      : (evidenceData?.observation_date || evidence?.acceptanceDetails?.date);
                                    confirmedBy = evidenceStatusUpdated
                                      ? evidenceData?.observationBy?.fullname
                                      : (evidenceData?.observationBy?.fullname || evidence?.acceptanceDetails?.name);
                                    break;

                                  default:
                                    isAccepted = false;
                                    confirmedDate = null;
                                    confirmedBy = null;
                                }

                                // Show confirmation info if accepted and we have the required details
                                if (isAccepted && confirmedDate && confirmedBy) {
                                  return (
                                    <div className='document_wrp confirmed_info align-items-start'>
                                      <i className="fal fa-history history_btn" />
                                      <small>
                                        Confirmed on <strong>{moment.unix(confirmedDate).format('DD MMM YY')}</strong> at <strong>{moment.unix(confirmedDate).format('HH:mm')}</strong> by <strong>{confirmedBy}</strong>
                                      </small>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>

                            <div className='main-wrp mb-0'>
                              {evidence?.items?.map((item, itemKey) => (
                                <div className='file-con' key={itemKey}>
                                  <div className='document_wrp image_info_con justify-content-between mb-0'>
                                    <div className="image_info">
                                      <i className={`fal ${ evidence.name == 'photo' ? "fa-file-image" :  "fa-file-video" }`} />
                                      <p className="mb-0 truncate-evidence-text">{item?.evidence_url?.split('/').pop() || 'File'}</p>
                                    </div>

                                    <div className="image-action-btn">
                                      <a
                                        href={
                                          item?.evidence_url?.startsWith('https://')
                                            ? item.evidence_url
                                            : item?.fileUploadedFrom == '1'
                                              ? AWS_BASE_URL + item.evidence_url
                                              : `${downloadURL}${IMAGE_URL.replace("https://", "")}/${item.evidence_url.replace("home/myaie/public_html/", "").replaceAll("public/", "")}`
                                          } download={true}
                                        className="btn btn-primary rounded-circle"
                                        title="Download"
                                        onClick={(e) => {
                                          e.preventDefault();
                                        
                                          Swal.fire({
                                            icon: "info",
                                            title: "Downloading...",
                                            text: "Please wait",
                                            allowOutsideClick: false,
                                            allowEscapeKey: false,
                                            didOpen: () => {
                                              Swal.showLoading();
                                            },
                                          });
                                        
                                          const link = e.currentTarget.href;
                                          const xhr = new XMLHttpRequest();
                                          xhr.open("GET", link, true);
                                          xhr.responseType = "blob";
                                        
                                          xhr.onload = function () {
                                            console.log("xhr.response", xhr.response);
                                            if (this.status === 200 && xhr.response) {
                                              const fileName = item?.evidence_url?.split("/").pop() || "download";
                                              const blob = new Blob([this.response]);
                                              const url = window.URL.createObjectURL(blob);
                                              const a = document.createElement("a");
                                              a.style.display = "none";
                                              a.href = url;
                                              a.download = fileName;
                                              document.body.appendChild(a);
                                              a.click();
                                              window.URL.revokeObjectURL(url);
                                              Swal.close();
                                            } else {
                                              Swal.fire({
                                                type : 'error',
                                                icon: "error",
                                                title: "Error",
                                                text: "Error downloading file",
                                              })
                                            }
                                          };
                                        
                                          xhr.onerror = function () {
                                            Swal.fire({
                                              type : 'error',
                                              icon: "error",
                                              title: "Error",
                                              text: "Error downloading file",
                                            })
                                          };
                                        
                                          xhr.ontimeout = function () {
                                            Swal.fire({
                                              type : 'error',
                                              icon: "error",
                                              title: "Error",
                                              text: "Error downloading file",
                                            })
                                          };
                                        
                                          xhr.timeout = 15000;
                                          xhr.send();
                                        }}
                                        
                                      >
                                        <i className="fal fa-download"></i>
                                      </a>
                                      <a
                                        href={
                                          item?.evidence_url?.startsWith('https://')
                                            ? encodeURI(item.evidence_url)
                                            : item?.fileUploadedFrom == '1'
                                              ? AWS_BASE_URL + encodeURI(item.evidence_url)
                                              : `${IMAGE_URL}/${encodeURI(createCleanFilePath(item.evidence_url))}`
                                        } target="_blank" className="btn btn-primary rounded-circle" title="View">
                                        <i className="fal fa-eye"></i>
                                      </a>

                                      <PermissionsGate scopes={assessmentHeader?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                                        <button
                                          className="btn btn-danger rounded-circle"
                                          title="Delete"
                                          style={{ visibility: item?.is_admin == 0 ? "hidden" : "visible" }}
                                          onClick={() => {
                                            if (item?.is_admin != 0)
                                              handleDeleteFile(item)
                                          }}
                                        >
                                          <i className="fal fa-trash-alt"></i>
                                        </button>
                                      </PermissionsGate>

                                    </div>
                                  </div>
                                  {item?.is_admin != 0 && (
                                    <div className='document_wrp align-items-start '>
                                      <i className="fal fa-history history_btn" />
                                      <small>
                                        {item?.uploadedBy
                                          ? <>
                                            Uploaded on <strong>{moment.unix(item?.evidence_uploaded_date).format('DD MMM YY')}</strong> at <strong>{moment.unix(item?.evidence_uploaded_date).format('HH:mm')}</strong> by <strong>{item?.uploadedBy?.fullname}</strong>
                                          </>
                                          : ""}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {(evidence.name === 'photo' || evidence.name === 'video') &&
                              Object.keys(uploadProgress[evidence.name]).length > 0 && (
                                <div className="main-wrp mb-0">
                                  {Object.entries(uploadProgress[evidence.name]).map(([fileName, progress], idx) => (
                                    <div className="file-upload-progress mb-2" key={idx}>
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small className="text-truncate" style={{ maxWidth: '80%' }}>{fileName}</small>
                                        <small>{progress}%</small>
                                      </div>
                                      <div className="progress" style={{ height: '6px' }}>
                                        <div
                                          className="progress-bar bg-success"
                                          role="progressbar"
                                          style={{ width: `${progress}%` }}
                                          aria-valuenow={progress}
                                          aria-valuemin="0"
                                          aria-valuemax="100">
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                            {evidence?.name !== "observation" && (
                              <div className="form-group atttach-file m-0">
                                <label className="btn upload-file" title="Upload File" htmlFor={`fileEvidence${evidence?.name}`}>
                                  <i className="fal fa-upload"></i> Upload File
                                  <PermissionsGate scopes={assessmentHeader?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                                    <input
                                      type="file"
                                      className="form-control  form-control-aatch-file"
                                      onChange={(e) => {
                                        handleUploadFile(e, evidence?.name)
                                        e.target.value = null
                                      }}
                                      id={`fileEvidence${evidence?.name}`}
                                      style={{ display: 'none' }}
                                      disabled={isUploading[evidence.name] || shouldLockAssessment}
                                      accept={
                                        evidence.name === "photo"
                                          ? "image/*"
                                          : evidence.name === "video"
                                            ? "video/*"
                                            : undefined
                                      }
                                    />
                                  </PermissionsGate>
                                </label>
                              </div>
                            )}
                          </div>
                        })()
                        : ""
                    ))}
                  </div>
                }
              </div>

              <div className='divider'></div>

              <div className='bottom-con'>
                {Number(assDetails?.grading_type) !== 4 ? <>
                  <h6 className='mb-0 heading'><i className="fal fa-file-alt" /> Result</h6>
                  <div className=''>
                    <p className='mb-2'>Result</p>
                    <div className='input-submission-col resultstatus_flex'>
                      <PermissionsGate scopes={assessmentHeader?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                        <input
                          className={`form-control ${error ? 'is-invalid' : ''}`}
                          type={"number"}
                          max={twoDecimalRound(selectedStudentDetails?.total_score)}
                          value={updatedData.score != null ? twoDecimalRound(updatedData.score) : ""}
                          disabled={!isMatchingQuestionType(["Document Upload", "Evidence Only", "Long Answer"]) || selectedStudentDetails?.lock_grades == 1}
                          onKeyDown={(e) => {
                            // Block +, -, and other non-numeric keys (allow numbers, decimal, backspace, delete, arrows)
                            if (isBlockedNumericKey(e.key)) e.preventDefault();
                          }}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
                              return; // Reject input if it contains anything other than digits and decimal point
                            }

                            if (isMatchingQuestionType(["Document Upload", "Evidence Only", "Long Answer"])) {
                              setUpdatedData((prev) => ({
                                ...prev,
                                score: value === "" ? null : Number(value),
                              }));
                              if (value > Number(twoDecimalRound(selectedStudentDetails?.total_score) || 0)) {
                                setError(true)
                              }
                              else {
                                setError(false)
                              }
                            }
                          }}

                        />
                      </PermissionsGate>
                      <p className='mb-0'>&nbsp; / {twoDecimalRound(selectedStudentDetails?.total_score) || 0}</p>
                    </div>
                  </div>
                </> : null
                }
                <div className='form-group-blk '>
                  <p className='mb-2'>Feedback</p>
                  <PermissionsGate scopes={assessmentHeader?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                    <input
                      className="form-control feedback-input"
                      type="text"
                      placeholder='Criteria Feedback'
                      value={updatedData.user_feedback ?? ""}
                      disabled={shouldLockAssessment}
                      onChange={(e) => {
                        const value = e.target.value || "";
                        setUpdatedData((prev) => ({
                          ...prev,
                          user_feedback: value,
                        }));
                      }}
                    />
                  </PermissionsGate>
                </div>
              </div>

              <div className="form-group form-group-save-cancel">
                <PermissionsGate scopes={assessmentHeader?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    title="Save"
                    onClick={handleSaveSubmission}
                    disabled={savingData}
                  >
                    {savingData ? <i className="fas fa-cog fa-spin"></i> : <i className="fal fa-save"></i>}
                    Save
                  </button>
                </PermissionsGate>
                <PermissionsGate scopes={assessmentHeader?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                  <button
                    type="button"
                    className="btn btn-danger"
                    title="Close"
                    disabled={savingData}
                    onClick={modalClose}
                  >
                    <i className="fal fa-times"></i>
                    Close
                  </button>
                </PermissionsGate>
                {error ? <p className='invalid-feedback d-block mb-0'>You can't put mark more then Total mark</p> : ""}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionModal;